import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Radar, RefreshCw, Loader2, BellRing, CheckCircle2, AlertTriangle, XCircle, HelpCircle } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/formatDate";

const TONE = {
  ok: { icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50/60", badge: "bg-emerald-100 text-emerald-800", label: "OK" },
  warn: { icon: AlertTriangle, cls: "border-amber-200 bg-amber-50/60", badge: "bg-amber-100 text-amber-800", label: "Warnung" },
  fail: { icon: XCircle, cls: "border-rose-300 bg-rose-50/70", badge: "bg-rose-600 text-white", label: "Störung" },
  unknown: { icon: HelpCircle, cls: "border-slate-200 bg-slate-50", badge: "bg-slate-100 text-slate-600", label: "Ungeprüft" },
};

export const IntegrationWatchCard = () => {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(() => {
    apiClient.get("/admin/integration-status").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      await apiClient.post("/admin/integration-status/run");
      toast.success("Alle Checks ausgeführt");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Prüfung fehlgeschlagen");
    } finally {
      setRunning(false);
    }
  };

  const testAlert = async () => {
    setTesting(true);
    try {
      const r = await apiClient.post("/admin/integration-status/test-alert");
      toast.success(`Testalarm an ${r.data.notified} Super-Admin(s) gesendet (Glocke, Push, E-Mail)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Testalarm fehlgeschlagen");
    } finally {
      setTesting(false);
    }
  };

  const checks = data?.checks || [];
  const known = Object.keys(data?.labels || {});
  const rows = known.map((k) => checks.find((c) => c.id === k) || { id: k, label: data.labels[k], status: "unknown", detail: "Noch nicht geprüft" });
  const s = data?.summary || {};

  return (
    <Card className="p-6" data-testid="integration-watch-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${s.fail ? "bg-rose-100 text-rose-700" : s.warn ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
            <Radar className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Integrations-Wächter</h3>
            <p className="mt-1 text-sm text-slate-500">
              Prüft alle 10 Minuten E-Mail, WhatsApp, EZB-Kurse, Bank-Schnittstelle, KI, Datenbank und Jobs.
              Bei Störung: sofortige Meldung an alle Super-Admins (Glocke, Push, E-Mail), Erinnerung alle 6 h, Entwarnung bei Behebung.
            </p>
            {data?.checked_at && (
              <p className="mt-1 text-xs text-slate-400" data-testid="integration-watch-checked-at">
                Letzte Prüfung {formatDateTime(data.checked_at)} · {s.ok || 0} OK · {s.warn || 0} Warnung · {s.fail || 0} Störung
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={testAlert} disabled={testing} data-testid="integration-watch-test-alert">
            {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <BellRing className="h-4 w-4 mr-1.5" />} Testalarm
          </Button>
          <Button size="sm" onClick={runNow} disabled={running} data-testid="integration-watch-run">
            {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Jetzt prüfen
          </Button>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {rows.map((c) => {
          const t = TONE[c.status] || TONE.unknown;
          const Icon = t.icon;
          return (
            <div key={c.id} className={`rounded-lg border p-3 ${t.cls}`} data-testid={`integration-check-${c.id}`} data-status={c.status}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-medium text-slate-900 leading-tight">{c.label}</div>
                <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${t.badge}`}>
                  <Icon className="h-3 w-3" /> {t.label}
                </span>
              </div>
              <div className="mt-1.5 text-xs text-slate-600 break-words" data-testid={`integration-check-detail-${c.id}`}>{c.detail}</div>
              {c.status !== "ok" && c.since && (
                <div className="mt-1 text-[10px] text-slate-500">seit {formatDateTime(c.since)}</div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default IntegrationWatchCard;
